import type { Container } from '@elara/core/di';
import type { AnyFlowSpec } from '@elara/core/flows';

import type { AttachedFlow, HttpFlowTransportOptions } from '#lib/flow-utils.js';

import { partial } from 'lodash-es';

import { createContainer } from '@elara/core/di';
import {
  DebugGetAgentStateFlowSpec,
  DebugGetChatStateFlowSpec,
  DebugGetSystemStatusFlowSpec,
  DebugGetSkillsFlowSpec,
  DebugGetToolsFlowSpec,
  DebugGetWorkingMemoryFlowSpec,
  DebugWarmupFlowSpec,
} from '@elara/domain/debug-flows';
import {
  AcceptPoliciesFlowSpec,
  AddItineraryItemFlowSpec,
  AddShortlistItemFlowSpec,
  BatchReorderShortlistGroupsFlowSpec,
  BatchReorderShortlistItemsFlowSpec,
  AddPaymentMethodFlowSpec,
  CancelHotelBookingFlowSpec,
  ClearBookingFlowSpec,
  ClearFlightBookingFlowSpec,
  CompleteOnboardingFlowSpec,
  CreateCardCaptureSessionFlowSpec,
  CreateChatFlowSpec,
  DecidePreferenceProposalFlowSpec,
  AddProfilePreferenceFlowSpec,
  UpdateProfilePreferenceFlowSpec,
  RemoveProfilePreferenceFlowSpec,
  SaveTravellerFlowSpec,
  RemoveTravellerFlowSpec,
  CreateDemoTripFlowSpec,
  CreateShortlistGroupFlowSpec,
  CreateTripFlowSpec,
  CreateUserProfileFlowSpec,
  DeleteChatFlowSpec,
  DeleteChatMessageFlowSpec,
  DeleteItineraryItemFlowSpec,
  DeletePaymentMethodFlowSpec,
  DeleteShortlistGroupFlowSpec,
  DeleteTripFlowSpec,
  DisableTripShareLinkFlowSpec,
  EnableTripShareLinkFlowSpec,
  EnrichHotelPhotosFlowSpec,
  EnrichPlaceFlowSpec,
  EnrichRoomOptionFlowSpec,
  GenerateItinerarySummariesFlowSpec,
  GeocodeAddressFlowSpec,
  GetAvailableModelsFlowSpec,
  GetDestinationWeatherSpec,
  GetFlightBookingOptionsFlowSpec,
  GetPromptVersionsFlowSpec,
  GetSkillVersionsFlowSpec,
  GetReferralCodeFlowSpec,
  MarkAllNotificationsReadFlowSpec,
  MarkNotificationReadFlowSpec,
  PrepareHotelCheckoutFlowSpec,
  RecheckFlightPriceFlowSpec,
  RecheckRoomPriceFlowSpec,
  SearchHotelRoomsFlowSpec,
  GetRoomSearchDefaultsFlowSpec,
  RecordBookingFlowSpec,
  RefreshPlaceSpec,
  RegisterFCMTokenFlowSpec,
  ReportChatPresenceFlowSpec,
  RemoveShortlistItemFlowSpec,
  RequestAccountDeletionFlowSpec,
  SendSmsWelcomeFlowSpec,
  ResetDemoTripFlowSpec,
  RestoreChatFlowSpec,
  RestoreItineraryItemFlowSpec,
  RestoreTripFlowSpec,
  SendMessageFlowSpec,
  GetMembershipPlansFlowSpec,
  CompleteMembershipPaymentFlowSpec,
  OpenBillingPortalFlowSpec,
  StartMembershipPaymentFlowSpec,
  SyncMembershipFlowSpec,
  StopGenerationFlowSpec,
  SubmitFlightBookingFlowSpec,
  SubmitHotelBookingFlowSpec,
  SubmitMessageFeedbackFlowSpec,
  UpdateBookingAttachmentsFlowSpec,
  UpdateBookingFlowSpec,
  UpdateItineraryItemFlowSpec,
  UpdatePaymentMethodFlowSpec,
  UpdateShortlistGroupFlowSpec,
  UpdateShortlistItemFlowSpec,
  UpdateTripFlowSpec,
  UpdateUserProfileSpec,
  WarmupFlowSpec,
} from '@elara/domain/flows';

import { attachFlowSpec, createFlowProxy } from '#lib/flow-utils.js';

const appFlowSpecs = {
  completeOnboarding: CompleteOnboardingFlowSpec,
  createDemoTrip: CreateDemoTripFlowSpec,
  resetDemoTrip: ResetDemoTripFlowSpec,
  createChat: CreateChatFlowSpec,
  decidePreferenceProposal: DecidePreferenceProposalFlowSpec,
  addProfilePreference: AddProfilePreferenceFlowSpec,
  updateProfilePreference: UpdateProfilePreferenceFlowSpec,
  removeProfilePreference: RemoveProfilePreferenceFlowSpec,
  saveTraveller: SaveTravellerFlowSpec,
  removeTraveller: RemoveTravellerFlowSpec,
  createTrip: CreateTripFlowSpec,
  updateTrip: UpdateTripFlowSpec,
  deleteTrip: DeleteTripFlowSpec,
  restoreTrip: RestoreTripFlowSpec,
  enableTripShareLink: EnableTripShareLinkFlowSpec,
  disableTripShareLink: DisableTripShareLinkFlowSpec,
  deleteChat: DeleteChatFlowSpec,
  deleteChatMessage: DeleteChatMessageFlowSpec,
  restoreChat: RestoreChatFlowSpec,
  sendMessage: SendMessageFlowSpec,
  stopGeneration: StopGenerationFlowSpec,
  submitMessageFeedback: SubmitMessageFeedbackFlowSpec,
  updateUserProfile: UpdateUserProfileSpec,
  requestAccountDeletion: RequestAccountDeletionFlowSpec,
  sendSmsWelcome: SendSmsWelcomeFlowSpec,
  createUserProfile: CreateUserProfileFlowSpec,
  getMembershipPlans: GetMembershipPlansFlowSpec,
  startMembershipPayment: StartMembershipPaymentFlowSpec,
  completeMembershipPayment: CompleteMembershipPaymentFlowSpec,
  openBillingPortal: OpenBillingPortalFlowSpec,
  syncMembership: SyncMembershipFlowSpec,
  acceptPolicies: AcceptPoliciesFlowSpec,
  getAvailableModels: GetAvailableModelsFlowSpec,
  getPromptVersions: GetPromptVersionsFlowSpec,
  getSkillVersions: GetSkillVersionsFlowSpec,
  addItineraryItem: AddItineraryItemFlowSpec,
  updateBookingAttachments: UpdateBookingAttachmentsFlowSpec,
  updateItineraryItem: UpdateItineraryItemFlowSpec,
  deleteItineraryItem: DeleteItineraryItemFlowSpec,
  restoreItineraryItem: RestoreItineraryItemFlowSpec,
  generateItinerarySummaries: GenerateItinerarySummariesFlowSpec,
  getDestinationWeather: GetDestinationWeatherSpec,
  geocodeAddress: GeocodeAddressFlowSpec,
  getFlightBookingOptions: GetFlightBookingOptionsFlowSpec,
  enrichRoomOption: EnrichRoomOptionFlowSpec,
  recheckFlightPrice: RecheckFlightPriceFlowSpec,
  recheckRoomPrice: RecheckRoomPriceFlowSpec,
  searchHotelRooms: SearchHotelRoomsFlowSpec,
  getRoomSearchDefaults: GetRoomSearchDefaultsFlowSpec,
  enrichPlace: EnrichPlaceFlowSpec,
  enrichHotelPhotos: EnrichHotelPhotosFlowSpec,
  // Shortlist flows
  addShortlistItem: AddShortlistItemFlowSpec,
  updateShortlistItem: UpdateShortlistItemFlowSpec,
  removeShortlistItem: RemoveShortlistItemFlowSpec,
  batchReorderShortlistItems: BatchReorderShortlistItemsFlowSpec,
  createShortlistGroup: CreateShortlistGroupFlowSpec,
  updateShortlistGroup: UpdateShortlistGroupFlowSpec,
  deleteShortlistGroup: DeleteShortlistGroupFlowSpec,
  batchReorderShortlistGroups: BatchReorderShortlistGroupsFlowSpec,
  refreshPlace: RefreshPlaceSpec,
  getReferralCode: GetReferralCodeFlowSpec,
  // Checkout flows
  prepareHotelCheckout: PrepareHotelCheckoutFlowSpec,
  submitHotelBooking: SubmitHotelBookingFlowSpec,
  cancelHotelBooking: CancelHotelBookingFlowSpec,
  submitFlightBooking: SubmitFlightBookingFlowSpec,
  clearFlightBooking: ClearFlightBookingFlowSpec,
  recordBooking: RecordBookingFlowSpec,
  updateBooking: UpdateBookingFlowSpec,
  clearBooking: ClearBookingFlowSpec,
  // Notification flows
  markNotificationRead: MarkNotificationReadFlowSpec,
  markAllNotificationsRead: MarkAllNotificationsReadFlowSpec,
  registerFCMToken: RegisterFCMTokenFlowSpec,
  reportChatPresence: ReportChatPresenceFlowSpec,
  // Wallet / saved payment methods (M4)
  createCardCaptureSession: CreateCardCaptureSessionFlowSpec,
  addPaymentMethod: AddPaymentMethodFlowSpec,
  updatePaymentMethod: UpdatePaymentMethodFlowSpec,
  deletePaymentMethod: DeletePaymentMethodFlowSpec,
  warmup: WarmupFlowSpec,
} as const;

const debugFlowSpecs = {
  getTools: DebugGetToolsFlowSpec,
  getSkills: DebugGetSkillsFlowSpec,
  getChatState: DebugGetChatStateFlowSpec,
  getSystemStatus: DebugGetSystemStatusFlowSpec,
  getAgentState: DebugGetAgentStateFlowSpec,
  getWorkingMemory: DebugGetWorkingMemoryFlowSpec,
  warmup: DebugWarmupFlowSpec,
} as const;

/**
 * Creates a flow container using the router proxy pattern.
 * All flow calls are routed through a single endpoint (appFlows or debugFlows)
 * to reduce cold start latency.
 */
export function createFlowsContainerWithProxy<F extends Flows>(
  routerName: string,
  flows: F,
  opts?: HttpFlowTransportOptions,
): $FlowsContainer<F> {
  const proxy = createFlowProxy(routerName, flows, opts);
  const $child = createContainer(routerName);

  // Wrap proxy values in container for DI compatibility
  const $flows = Object.entries(proxy).reduce((container, [name, callable]) => {
    return container.provided(name, callable);
  }, $child);

  return $flows as $FlowsContainer<F>;
}

/**
 * @deprecated Use createFlowsContainerWithProxy instead for router-based flow routing.
 * Creates a flow container using individual endpoints (one per flow).
 */
export function createFlowsContainer<F extends Flows>(
  name: string,
  flows: F,
  opts?: HttpFlowTransportOptions,
): $FlowsContainer<F> {
  const $child = createContainer(name);
  const $flows = Object.entries(flows).reduce((container, [name, spec]) => {
    return container.providedLazy(name, () => attachFlowSpec(spec, opts));
  }, $child);
  return $flows as $FlowsContainer<F>;
}

// Use router proxy for app flows (consolidated endpoint)
export const createAppFlowsContainer = partial(createFlowsContainerWithProxy, 'appFlows', appFlowSpecs);
export type $AppFlowsContainer = ReturnType<typeof createAppFlowsContainer>;
export type AppFlowsContainer = $AppFlowsContainer['use'];

// Use router proxy for debug flows (consolidated endpoint)
export const createDebugFlowsContainer = partial(createFlowsContainerWithProxy, 'debugFlows', debugFlowSpecs);
export type $DebugFlowsContainer = ReturnType<typeof createDebugFlowsContainer>;
export type DebugFlowsContainer = $DebugFlowsContainer['use'];

export type Flows = Record<string, AnyFlowSpec>;

export type $FlowsContainer<F extends Flows> = Container<{ [K in keyof F]: AttachedFlow<F[K]> }>;
export type FlowsContainer<F extends Flows> = $FlowsContainer<F>['use'];
